import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { ReportsService, type DashboardReport } from '../../../services/reports.service';
import { AlertService } from '../../../services/alert.service';
import Icon from '../../../components/forms/icon.component';
import DatePicker from '../../../components/forms/date-picker.component';
import { HangupCausesChart, CPD_COLORS } from './dashboard.charts';
import { num, pct, pctRate } from '../../../components/utils/format';

// humanRate/voicemailRate del summary vienen como fraccion 0..1 -> pctRate.
const secs = (s: number | undefined) => {
  if (!s) return '0 min';
  const m = Math.round(s / 60);
  return m >= 60 ? `${(m / 60).toFixed(1)} h` : `${m} min`;
};

/**
 * Histórico (ClickHouse) vía GET /v1/reports/dashboard: summary + top operadores + top rutas +
 * hangup causes para un rango de fechas. Por defecto hoy.
 */
export default function HistoricalSection() {
  const [range, setRange] = useState({
    from: moment().format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD')
  });
  const [data, setData] = useState<DashboardReport | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ReportsService.getDashboard({ from: range.from, to: range.to, limit: 10 });
      setData(res);
    } catch (error: any) {
      AlertService.toastError(error?.error || 'No se pudo cargar el histórico');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.summary;

  return (
    <div className="card shadow-sm border-0 rounded-4 mt-6">
      <div className="card-header border-0 pt-6 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h3 className="card-title fw-bolder fs-3 mb-1">Histórico</h3>
          <div className="text-muted fs-7">
            Cierre por día desde ClickHouse ·{' '}
            {range.from === range.to ? range.from : `${range.from} → ${range.to}`}
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          <DatePicker
            initialStart={range.from}
            initialEnd={range.to}
            onDatesChange={(r: any) =>
              setRange({ from: r.start.format('YYYY-MM-DD'), to: r.end.format('YYYY-MM-DD') })
            }
          />
          <button
            type="button"
            className="btn btn-icon btn-light hover-scale rounded-circle"
            onClick={load}
            disabled={loading}
            title="Recargar"
          >
            <Icon icon="ki-arrows-circle" size={loading ? 'fs-2 spin' : 'fs-2'} />
          </button>
        </div>
      </div>

      <div className="card-body pt-4">
        <div className="row g-4 mb-6">
          {[
            { label: 'Llamadas', value: num(s?.total), color: 'primary' },
            { label: 'Contestadas', value: num(s?.answered), color: 'info' },
            {
              label: 'Humano',
              value: `${num(s?.human)} · ${pctRate(s?.humanRate)}`,
              color: 'success'
            },
            {
              label: 'Buzón (603)',
              value: `${num(s?.voicemail)} · ${pctRate(s?.voicemailRate)}`,
              color: 'danger'
            },
            { label: 'Sin veredicto', value: num(s?.unknown), color: 'secondary' },
            { label: 'Bypass', value: num(s?.bypassed), color: 'primary' },
            { label: 'Facturable (billsec)', value: secs(s?.billsecSum), color: 'dark' }
          ].map((k) => (
            <div className="col-6 col-md-4 col-xl" key={k.label}>
              <div
                className={`border border-dashed border-gray-300 rounded-3 p-4 h-100 bg-light-${k.color}`}
              >
                <div className="fs-8 text-muted fw-semibold text-uppercase">{k.label}</div>
                <div className={`fs-3 fw-bolder text-${k.color}`}>{k.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-6">
          <div className="col-xl-4">
            <div className="fs-7 fw-bold text-gray-600 mb-2">Top operadores</div>
            <div className="table-responsive">
              <table className="table table-row-dashed table-sm fs-7 align-middle">
                <thead>
                  <tr className="text-muted fw-bold">
                    <th>Operador</th>
                    <th className="text-end">Llam.</th>
                    <th className="text-end" style={{ color: CPD_COLORS.human }}>
                      Hum.
                    </th>
                    <th className="text-end" style={{ color: CPD_COLORS.voicemail }}>
                      Buzón
                    </th>
                    <th className="text-end">% buzón</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.topOperators ?? []).map((r) => {
                    const def = r.human + r.voicemail;
                    return (
                      <tr key={`${r.day}-${r.operatorId}`}>
                        <td className="fw-bold text-uppercase">{r.operatorId || '—'}</td>
                        <td className="text-end">{num(r.total)}</td>
                        <td className="text-end">{num(r.human)}</td>
                        <td className="text-end">{num(r.voicemail)}</td>
                        <td className="text-end">{def ? pct((r.voicemail / def) * 100) : '—'}</td>
                      </tr>
                    );
                  })}
                  {!loading && (data?.topOperators ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-muted text-center py-4">
                        Sin datos en el rango
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="col-xl-4">
            <div className="fs-7 fw-bold text-gray-600 mb-2">Top rutas (grupo OpenSIPS)</div>
            <div className="table-responsive">
              <table className="table table-row-dashed table-sm fs-7 align-middle">
                <thead>
                  <tr className="text-muted fw-bold">
                    <th>Ruta</th>
                    <th className="text-end">Llam.</th>
                    <th className="text-end" style={{ color: CPD_COLORS.human }}>
                      Hum.
                    </th>
                    <th className="text-end" style={{ color: CPD_COLORS.voicemail }}>
                      Buzón
                    </th>
                    <th className="text-end">% buzón</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.topRoutes ?? []).map((r) => {
                    const def = r.human + r.voicemail;
                    return (
                      <tr key={`${r.day}-${r.routeGroupId}`}>
                        <td className="fw-bold">grupo {r.routeGroupId}</td>
                        <td className="text-end">{num(r.total)}</td>
                        <td className="text-end">{num(r.human)}</td>
                        <td className="text-end">{num(r.voicemail)}</td>
                        <td className="text-end">{def ? pct((r.voicemail / def) * 100) : '—'}</td>
                      </tr>
                    );
                  })}
                  {!loading && (data?.topRoutes ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-muted text-center py-4">
                        Sin datos en el rango
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="col-xl-4">
            <div className="fs-7 fw-bold text-gray-600 mb-2">Hangup causes</div>
            <HangupCausesChart
              rows={(data?.topHangupCauses ?? []).map((h) => ({
                key: h.hangupCause,
                count: h.count
              }))}
              height={260}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
